import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

// ─── Maya Learning Tab ────────────────────────────────────────────────────────

function MayaLearningTab({ campaign, api }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // 'url' | 'text' | 'file'
  const [urlInput, setUrlInput] = useState('');
  const [urlTitle, setUrlTitle] = useState('');
  const [textTitle, setTextTitle] = useState('');
  const [textContent, setTextContent] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const fetchSources = async () => {
    try {
      const res = await api.get(`/maya/knowledge/${campaign.id}`);
      setSources(res.data || []);
    } catch { toast.error('Erro ao carregar base de conhecimento'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSources(); }, [campaign.id]);

  const handleAddUrl = async () => {
    if (!urlInput.trim()) { toast.error('Informe a URL'); return; }
    setSaving(true);
    try {
      await api.post(`/maya/knowledge/${campaign.id}/url`, { url: urlInput.trim(), title: urlTitle.trim() || urlInput.trim() });
      toast.success('URL adicionada e processada pela Maya!');
      setUrlInput(''); setUrlTitle(''); setMode(null);
      fetchSources();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao adicionar URL'); }
    finally { setSaving(false); }
  };

  const handleAddText = async () => {
    if (!textContent.trim()) { toast.error('Informe o conteúdo'); return; }
    setSaving(true);
    try {
      await api.post(`/maya/knowledge/${campaign.id}/text`, { title: textTitle.trim() || 'Texto manual', content: textContent.trim() });
      toast.success('Texto adicionado à base da Maya!');
      setTextTitle(''); setTextContent(''); setMode(null);
      fetchSources();
    } catch { toast.error('Erro ao adicionar texto'); }
    finally { setSaving(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Arquivo muito grande (máx. 10MB)'); return; }
    setSaving(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/maya/knowledge/${campaign.id}/file`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`"${file.name}" adicionado à base da Maya!`);
      setMode(null);
      fetchSources();
    } catch { toast.error('Erro ao enviar arquivo'); }
    finally { setSaving(false); e.target.value = ''; }
  };

  const handleDelete = async (sourceId) => {
    try {
      await api.delete(`/maya/knowledge/${campaign.id}/${sourceId}`);
      toast.success('Fonte removida');
      fetchSources();
    } catch { toast.error('Erro ao remover'); }
  };

  const typeIcon = { url: '🌐', file: '📎', text: '📝' };
  const typeLabel = { url: 'Site', file: 'Arquivo', text: 'Texto' };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">🧠 Aprendizado Maya</h3>
          <p className="text-sm text-gray-500 mt-0.5">Adicione fontes de conhecimento para a Maya aprender sobre seu negócio e personalizar as conversas desta campanha.</p>
        </div>
      </div>

      {/* Add source buttons */}
      {!mode && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setMode('url')} className="bg-blue-600 hover:bg-blue-700 text-white">🌐 Adicionar Site</Button>
          <Button size="sm" variant="outline" onClick={() => setMode('file')}>📎 Anexar Arquivo</Button>
          <Button size="sm" variant="outline" onClick={() => setMode('text')}>📝 Texto Manual</Button>
        </div>
      )}

      {/* URL Form */}
      {mode === 'url' && (
        <Card className="border-blue-200 bg-blue-50/40">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-semibold text-blue-900">🌐 Adicionar URL de site</p>
            <div>
              <Label className="text-xs">URL do site *</Label>
              <Input className="mt-1 text-sm" placeholder="https://empresa.com.br" value={urlInput} onChange={e => setUrlInput(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Título (opcional)</Label>
              <Input className="mt-1 text-sm" placeholder="Ex: Site da empresa cliente" value={urlTitle} onChange={e => setUrlTitle(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddUrl} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                {saving ? '⏳ Processando…' : '✓ Adicionar e Processar'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMode(null)}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* File Form */}
      {mode === 'file' && (
        <Card className="border-purple-200 bg-purple-50/40">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-semibold text-purple-900">📎 Anexar arquivo</p>
            <p className="text-xs text-purple-700">PDF, DOCX, TXT, imagens — máx. 10MB. Textos e PDFs serão lidos pela Maya.</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => fileRef.current?.click()} disabled={saving} className="bg-purple-600 hover:bg-purple-700 text-white">
                {saving ? '⏳ Enviando…' : '📂 Escolher arquivo'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMode(null)}>Cancelar</Button>
            </div>
            <input ref={fileRef} type="file" className="hidden" onChange={handleFileUpload} accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.mp3,.mp4,.ppt,.pptx" />
          </CardContent>
        </Card>
      )}

      {/* Text Form */}
      {mode === 'text' && (
        <Card className="border-green-200 bg-green-50/40">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-semibold text-green-900">📝 Texto manual</p>
            <div>
              <Label className="text-xs">Título</Label>
              <Input className="mt-1 text-sm" placeholder="Ex: Proposta de valor" value={textTitle} onChange={e => setTextTitle(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Conteúdo *</Label>
              <textarea
                className="mt-1 w-full border border-gray-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-400"
                rows={5}
                placeholder="Descreva o negócio, produto, diferenciais, dores do cliente…"
                value={textContent}
                onChange={e => setTextContent(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddText} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">
                {saving ? '⏳ Salvando…' : '✓ Adicionar'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMode(null)}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sources list */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Base de conhecimento ({sources.length})</p>
        {loading ? (
          <div className="text-center py-8 text-gray-400">⏳ Carregando…</div>
        ) : sources.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-gray-400">
            <p className="text-3xl mb-2">🧠</p>
            <p className="font-medium text-sm">A Maya ainda não tem conhecimento sobre esta campanha</p>
            <p className="text-xs mt-1">Adicione sites, arquivos ou textos para ela aprender</p>
          </CardContent></Card>
        ) : (
          sources.map(s => (
            <div key={s.id} className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 hover:border-blue-200 transition-colors">
              <span className="text-xl mt-0.5">{typeIcon[s.type] || '📄'}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-800 truncate">{s.title}</p>
                  <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{typeLabel[s.type]}</span>
                  {s.file_size && <span className="text-xs text-gray-400">{(s.file_size / 1024).toFixed(0)} KB</span>}
                </div>
                {s.url && <p className="text-xs text-blue-500 truncate">{s.url}</p>}
                {s.content && <p className="text-xs text-gray-400 mt-0.5 truncate">{s.content.slice(0, 120)}…</p>}
                <p className="text-xs text-gray-300 mt-1">{new Date(s.created_at).toLocaleDateString('pt-BR')}</p>
              </div>
              <button onClick={() => handleDelete(s.id)} className="text-gray-300 hover:text-red-500 transition-colors text-lg leading-none mt-0.5">×</button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ─── Flow Builder Tab ─────────────────────────────────────────────────────────

const STEP_TYPES = [
  { type: 'message', label: 'Mensagem', icon: '💬', color: 'blue' },
  { type: 'audio',   label: 'Áudio',   icon: '🎙️', color: 'purple' },
  { type: 'image',   label: 'Imagem',  icon: '🖼️', color: 'green' },
  { type: 'file',    label: 'Arquivo', icon: '📎', color: 'amber' },
  { type: 'wait',    label: 'Aguardar',icon: '⏱️', color: 'gray' },
];

function StepCard({ step, index, onUpdate, onDelete, campaignId, flowId, api }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const def = STEP_TYPES.find(t => t.type === step.type) || STEP_TYPES[0];
  const colorMap = { blue: 'border-blue-200 bg-blue-50/30', purple: 'border-purple-200 bg-purple-50/30', green: 'border-green-200 bg-green-50/30', amber: 'border-amber-200 bg-amber-50/30', gray: 'border-gray-200 bg-gray-50' };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/maya/flows/${campaignId}/${flowId}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUpdate({ ...step, media_url: res.data.media_url, media_name: res.data.media_name });
      toast.success('Mídia enviada!');
    } catch { toast.error('Erro ao enviar mídia'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <div className={`border rounded-xl p-4 space-y-3 ${colorMap[def.color]}`}>
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 text-xs flex items-center justify-center font-bold">{index + 1}</span>
        <span className="text-base">{def.icon}</span>
        <span className="text-sm font-semibold text-gray-700">{def.label}</span>
        <div className="ml-auto flex gap-1">
          <button onClick={onDelete} className="text-gray-300 hover:text-red-500 transition-colors text-lg leading-none">×</button>
        </div>
      </div>

      {step.type === 'message' && (
        <textarea
          className="w-full border border-gray-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
          rows={3}
          placeholder="Escreva a mensagem que a Maya vai enviar…"
          value={step.content || ''}
          onChange={e => onUpdate({ ...step, content: e.target.value })}
        />
      )}

      {(step.type === 'audio' || step.type === 'image' || step.type === 'file') && (
        <div className="space-y-2">
          {step.media_name ? (
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
              <span className="text-sm">{def.icon}</span>
              <span className="text-sm text-gray-700 flex-1 truncate">{step.media_name}</span>
              <button onClick={() => onUpdate({ ...step, media_url: null, media_name: null })} className="text-gray-300 hover:text-red-500 text-lg">×</button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? '⏳ Enviando…' : `📂 ${step.type === 'audio' ? 'Enviar áudio' : step.type === 'image' ? 'Enviar imagem' : 'Enviar arquivo'}`}
            </Button>
          )}
          {step.type !== 'audio' && (
            <textarea
              className="w-full border border-gray-200 rounded-lg p-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
              rows={2}
              placeholder="Legenda / mensagem acompanhante (opcional)"
              value={step.content || ''}
              onChange={e => onUpdate({ ...step, content: e.target.value })}
            />
          )}
          <input ref={fileRef} type="file" className="hidden" onChange={handleMediaUpload}
            accept={step.type === 'audio' ? 'audio/*' : step.type === 'image' ? 'image/*' : '*'} />
        </div>
      )}

      {step.type === 'wait' && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Aguardar</span>
          <Input
            type="number" min={1} className="w-20 text-sm"
            value={step.delay_minutes || 60}
            onChange={e => onUpdate({ ...step, delay_minutes: parseInt(e.target.value) || 60 })}
          />
          <span className="text-sm text-gray-600">minutos antes do próximo passo</span>
        </div>
      )}
    </div>
  );
}

function FlowEditor({ flow, campaignId, api, onSaved, onClose }) {
  const [steps, setSteps] = useState(flow?.steps || []);
  const [name, setName] = useState(flow?.name || '');
  const [saving, setSaving] = useState(false);

  const addStep = (type) => {
    const newStep = { id: `tmp-${Date.now()}`, type, content: '', media_url: null, media_name: null, delay_minutes: 60, order: steps.length };
    setSteps(prev => [...prev, newStep]);
  };

  const updateStep = (index, updated) => {
    setSteps(prev => prev.map((s, i) => i === index ? updated : s));
  };

  const deleteStep = (index) => {
    setSteps(prev => prev.filter((_, i) => i !== index));
  };

  const moveStep = (index, dir) => {
    const newSteps = [...steps];
    const target = index + dir;
    if (target < 0 || target >= newSteps.length) return;
    [newSteps[index], newSteps[target]] = [newSteps[target], newSteps[index]];
    setSteps(newSteps.map((s, i) => ({ ...s, order: i })));
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Dê um nome ao fluxo'); return; }
    setSaving(true);
    try {
      const payload = { name, steps: steps.map((s, i) => ({ ...s, order: i })) };
      if (flow?.id) {
        await api.put(`/maya/flows/${campaignId}/${flow.id}`, payload);
      } else {
        const res = await api.post(`/maya/flows/${campaignId}`, { name });
        const newFlow = res.data;
        await api.put(`/maya/flows/${campaignId}/${newFlow.id}`, payload);
      }
      toast.success('Fluxo salvo!');
      onSaved();
      onClose();
    } catch { toast.error('Erro ao salvar fluxo'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-lg">←</button>
        <Input className="text-sm font-semibold flex-1" placeholder="Nome do fluxo…" value={name} onChange={e => setName(e.target.value)} />
        <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
          {saving ? '⏳' : '💾 Salvar'}
        </Button>
      </div>

      {/* Steps */}
      <div className="space-y-2">
        {steps.length === 0 ? (
          <div className="text-center py-8 text-gray-400 border-2 border-dashed rounded-xl">
            <p className="text-3xl mb-2">💬</p>
            <p className="text-sm">Adicione passos ao fluxo abaixo</p>
          </div>
        ) : (
          steps.map((step, index) => (
            <div key={step.id} className="relative">
              <div className="flex items-center gap-1 absolute -left-8 top-4 flex-col">
                <button onClick={() => moveStep(index, -1)} className="text-gray-300 hover:text-gray-600 text-xs" disabled={index === 0}>▲</button>
                <button onClick={() => moveStep(index, 1)} className="text-gray-300 hover:text-gray-600 text-xs" disabled={index === steps.length - 1}>▼</button>
              </div>
              <StepCard
                step={step} index={index}
                onUpdate={(updated) => updateStep(index, updated)}
                onDelete={() => deleteStep(index)}
                campaignId={campaignId} flowId={flow?.id}
                api={api}
              />
              {index < steps.length - 1 && (
                <div className="flex justify-center my-1"><div className="w-0.5 h-4 bg-gray-200" /></div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add step buttons */}
      <div className="border-t pt-4">
        <p className="text-xs text-gray-500 font-semibold mb-2">+ Adicionar passo</p>
        <div className="flex flex-wrap gap-2">
          {STEP_TYPES.map(t => (
            <button key={t.type} onClick={() => addStep(t.type)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:border-blue-300 hover:bg-blue-50 transition-all">
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function FlowBuilderTab({ campaign, api }) {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | flow object | 'new'

  const fetchFlows = async () => {
    try {
      const res = await api.get(`/maya/flows/${campaign.id}`);
      setFlows(res.data || []);
    } catch { toast.error('Erro ao carregar fluxos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFlows(); }, [campaign.id]);

  const handleDelete = async (flow) => {
    if (!window.confirm(`Remover fluxo "${flow.name}"?`)) return;
    try {
      await api.delete(`/maya/flows/${campaign.id}/${flow.id}`);
      toast.success('Fluxo removido');
      fetchFlows();
    } catch { toast.error('Erro ao remover'); }
  };

  const handleToggleActive = async (flow) => {
    try {
      await api.put(`/maya/flows/${campaign.id}/${flow.id}`, { is_active: !flow.is_active });
      toast.success(flow.is_active ? 'Fluxo desativado' : 'Fluxo ativado');
      fetchFlows();
    } catch { toast.error('Erro ao atualizar'); }
  };

  if (editing !== null) {
    return (
      <div className="pl-8">
        <FlowEditor
          flow={editing === 'new' ? null : editing}
          campaignId={campaign.id}
          api={api}
          onSaved={fetchFlows}
          onClose={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">💬 Fluxo de Conversa</h3>
          <p className="text-sm text-gray-500 mt-0.5">Defina o roteiro que a Maya seguirá nas conversas com os prospects desta campanha. Inclua mensagens, áudios, imagens e arquivos.</p>
        </div>
        <Button size="sm" onClick={() => setEditing('new')} className="bg-blue-600 hover:bg-blue-700 text-white">+ Novo fluxo</Button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-400">⏳ Carregando…</div>
      ) : flows.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <p className="text-4xl mb-3">💬</p>
          <p className="font-semibold text-gray-700">Nenhum fluxo criado</p>
          <p className="text-sm text-gray-500 mt-1">Crie um fluxo de conversa para a Maya seguir como SDR</p>
          <Button className="mt-4 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setEditing('new')}>+ Criar primeiro fluxo</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {flows.map(flow => (
            <Card key={flow.id} className={`border transition-colors ${flow.is_active ? 'border-blue-200' : 'border-gray-200'}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900 text-sm">{flow.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${flow.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {flow.is_active ? '● Ativo' : '○ Inativo'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {flow.steps?.length || 0} passos · Criado {new Date(flow.created_at).toLocaleDateString('pt-BR')}
                    </p>
                    {flow.steps?.length > 0 && (
                      <div className="flex gap-1 mt-1.5 flex-wrap">
                        {flow.steps.map(s => {
                          const def = STEP_TYPES.find(t => t.type === s.type);
                          return <span key={s.id} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{def?.icon} {def?.label}</span>;
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => handleToggleActive(flow)} className="text-xs">
                      {flow.is_active ? '⏸ Pausar' : '▶ Ativar'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(flow)} className="text-xs">✏️ Editar</Button>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(flow)} className="text-xs text-red-500 border-red-200 hover:bg-red-50">🗑️</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Maya Chat Tab ────────────────────────────────────────────────────────────

function MayaChatTab({ campaign, api }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [groqOk, setGroqOk] = useState(null);
  const bottomRef = useRef();

  useEffect(() => {
    api.get('/maya/status').then(r => setGroqOk(r.data?.groq_configured)).catch(() => setGroqOk(false));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setSending(true);
    try {
      const res = await api.post(`/maya/chat/${campaign.id}`, {
        message: userMsg,
        history: messages.slice(-10),
      });
      const { reply, interest_level, lead_created } = res.data;
      setMessages(prev => [...prev, { role: 'assistant', content: reply, interest_level, lead_created }]);
      if (lead_created) toast.success('🎯 Lead criado automaticamente no CRM pela Maya!');
    } catch { toast.error('Erro ao chamar Maya'); }
    finally { setSending(false); }
  };

  const interestColor = { alto: 'text-green-700 bg-green-100', médio: 'text-amber-700 bg-amber-100', baixo: 'text-gray-600 bg-gray-100' };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold text-gray-900">🤖 Testar Maya</h3>
        <p className="text-sm text-gray-500 mt-0.5">Simule uma conversa com a Maya para validar como ela responderá aos prospects usando o conhecimento desta campanha.</p>
      </div>

      {groqOk === false && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          ⚠️ Groq não configurado. Verifique a variável <code>GROQ_API_KEY</code> no .env.
        </div>
      )}

      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-4 py-3 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm">M</div>
          <div>
            <p className="text-white font-semibold text-sm">Maya</p>
            <p className="text-white/70 text-xs">SDR Inteligente · Campanha: {campaign.name}</p>
          </div>
          <div className="ml-auto">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-white/80 text-xs">Online</span>
            </div>
          </div>
        </div>

        <div className="h-80 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {messages.length === 0 && (
            <div className="text-center py-10 text-gray-400">
              <p className="text-3xl mb-2">👋</p>
              <p className="text-sm">Comece uma conversa para testar a Maya</p>
              <p className="text-xs mt-1">Ela usará o conhecimento desta campanha para responder</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-xs lg:max-w-md px-4 py-2.5 rounded-2xl text-sm ${m.role === 'user' ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm'}`}>
                <p className="whitespace-pre-wrap">{m.content}</p>
                {m.interest_level && (
                  <div className="mt-1.5 flex gap-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${interestColor[m.interest_level] || 'text-gray-600 bg-gray-100'}`}>
                      🎯 Interesse {m.interest_level}
                    </span>
                    {m.lead_created && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">✅ Lead criado no CRM</span>}
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm">
                <div className="flex gap-1 items-center">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="border-t border-gray-200 bg-white p-3 flex gap-2">
          <input
            className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            placeholder="Simule a resposta de um prospect…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            disabled={sending}
          />
          <Button size="sm" onClick={handleSend} disabled={sending || !input.trim()} className="bg-blue-600 hover:bg-blue-700 text-white px-4">
            {sending ? '⏳' : '▶'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Conversations Tab ────────────────────────────────────────────────────────

const INTEREST_COLORS = {
  alto:  'bg-green-100 text-green-700 border-green-200',
  médio: 'bg-amber-100 text-amber-700 border-amber-200',
  baixo: 'bg-gray-100 text-gray-600 border-gray-200',
};

function ConversationChat({ campaign, api, conv: initialConv, onBack }) {
  const [conv, setConv] = useState(initialConv);
  const [messages, setMessages] = useState(initialConv.messages || []);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [toggling, setToggling] = useState(false);
  const bottomRef = useRef();

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const reload = async () => {
    try {
      const r = await api.get(`/maya/conversations/${campaign.id}/${conv.id}`);
      setConv(r.data);
      setMessages(r.data.messages || []);
    } catch {}
  };

  const handleToggleHuman = async () => {
    setToggling(true);
    try {
      const r = await api.post(`/maya/conversations/${campaign.id}/${conv.id}/toggle-human-mode`, {
        human_mode: !conv.human_mode,
      });
      setConv(c => ({ ...c, human_mode: r.data.human_mode }));
      await reload();
      toast.success(r.data.human_mode ? '🧑 Você assumiu a conversa' : '🤖 Maya retomou o controle');
    } catch { toast.error('Erro ao alternar modo'); }
    finally { setToggling(false); }
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const txt = input.trim();
    setInput('');
    setSending(true);
    try {
      if (conv.human_mode) {
        // Human sends message directly
        const r = await api.post(`/maya/conversations/${campaign.id}/${conv.id}/human-message`, { content: txt });
        setMessages(prev => [...prev, r.data.message]);
      } else {
        // Maya responds
        setMessages(prev => [...prev, { role: 'user', content: txt, ts: new Date().toISOString() }]);
        const r = await api.post(`/maya/chat/${campaign.id}`, {
          message: txt,
          invitation_id: conv.invitation_id,
          prospect_name: conv.prospect_name,
          prospect_headline: conv.prospect_headline,
          prospect_url: conv.prospect_url,
        });
        if (r.data.reply) {
          setMessages(prev => [...prev, { role: 'assistant', content: r.data.reply, ts: new Date().toISOString(), interest_level: r.data.interest_level, lead_created: r.data.lead_created }]);
          if (r.data.lead_created) toast.success('🎯 Lead criado no CRM pela Maya!');
        }
      }
    } catch { toast.error('Erro ao enviar mensagem'); }
    finally { setSending(false); }
  };

  const fmtTime = ts => { try { return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

  return (
    <div className="flex flex-col h-full space-y-0">
      {/* Header */}
      <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-700 text-lg">←</button>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold shrink-0">
          {(conv.prospect_name || '?').charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-gray-900 text-sm">{conv.prospect_name}</p>
            {conv.interest_level && (
              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${INTEREST_COLORS[conv.interest_level] || 'bg-gray-100 text-gray-600'}`}>
                🎯 Interesse {conv.interest_level}
              </span>
            )}
            {conv.lead_created && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 font-medium">✅ Lead no CRM</span>}
          </div>
          {conv.prospect_headline && <p className="text-xs text-gray-500 truncate">{conv.prospect_headline}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {conv.prospect_url && (
            <a href={conv.prospect_url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:text-blue-700">🔗 LinkedIn</a>
          )}
          <button
            onClick={handleToggleHuman}
            disabled={toggling}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
              conv.human_mode
                ? 'bg-orange-500 text-white border-orange-500 hover:bg-orange-600'
                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'
            }`}
          >
            {toggling ? '⏳' : conv.human_mode ? '🧑 Você está no controle' : '🤖 Maya no controle'}
            <span className="ml-1 opacity-70">{conv.human_mode ? '→ devolver para Maya' : '→ assumir'}</span>
          </button>
          <button onClick={reload} className="text-gray-400 hover:text-gray-600 text-sm" title="Atualizar">🔄</button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-3 min-h-0" style={{ maxHeight: '420px' }}>
        {messages.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <p className="text-3xl mb-2">💬</p>
            <p className="text-sm">Nenhuma mensagem ainda nesta conversa</p>
          </div>
        )}
        {messages.map((m, i) => {
          if (m.role === 'system') {
            return (
              <div key={i} className="flex justify-center">
                <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{m.content}</span>
              </div>
            );
          }
          const isUser = m.role === 'user';
          const isHuman = m.by_human;
          return (
            <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
              {!isUser && (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0 mr-2 mt-1">
                  {isHuman ? '🧑' : 'M'}
                </div>
              )}
              <div className={`max-w-xs lg:max-w-md space-y-1`}>
                <div className={`px-4 py-2.5 rounded-2xl text-sm ${
                  isUser
                    ? 'bg-blue-600 text-white rounded-br-sm'
                    : isHuman
                      ? 'bg-orange-500 text-white rounded-bl-sm shadow-sm'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm'
                }`}>
                  <p className="whitespace-pre-wrap">{m.content}</p>
                  {m.interest_level && (
                    <div className="mt-1.5 flex gap-1 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isUser ? 'bg-blue-500 text-white' : (INTEREST_COLORS[m.interest_level] || 'bg-gray-100 text-gray-600')}`}>
                        🎯 Interesse {m.interest_level}
                      </span>
                      {m.lead_created && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">✅ Lead criado</span>}
                    </div>
                  )}
                </div>
                <p className={`text-xs text-gray-400 ${isUser ? 'text-right' : ''}`}>
                  {isHuman ? `${m.human_name || 'Operador'} · ` : isUser ? '' : 'Maya · '}
                  {fmtTime(m.ts)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 pt-3">
        {conv.human_mode && (
          <div className="mb-2 flex items-center gap-2 text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
            <span>🧑</span>
            <span>Você está no controle — suas mensagens serão enviadas como operador humano</span>
          </div>
        )}
        <div className="flex gap-2">
          <input
            className="flex-1 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            placeholder={conv.human_mode ? 'Digite sua mensagem como operador…' : 'Simule a resposta do prospect para Maya continuar…'}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
            disabled={sending}
          />
          <Button size="sm" onClick={handleSend} disabled={sending || !input.trim()}
            className={conv.human_mode ? 'bg-orange-500 hover:bg-orange-600 text-white px-4' : 'bg-blue-600 hover:bg-blue-700 text-white px-4'}>
            {sending ? '⏳' : '▶'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConversationsTab({ campaign, api }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openConv, setOpenConv] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | human | maya | lead

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/maya/conversations/${campaign.id}`);
      setConversations(r.data || []);
    } catch { toast.error('Erro ao carregar conversas'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchConversations(); }, [campaign.id]);

  const filtered = conversations.filter(c => {
    const matchSearch = !search || (c.prospect_name || '').toLowerCase().includes(search.toLowerCase());
    if (filter === 'human') return matchSearch && c.human_mode;
    if (filter === 'maya') return matchSearch && !c.human_mode;
    if (filter === 'lead') return matchSearch && c.lead_created;
    return matchSearch;
  });

  const counts = {
    all: conversations.length,
    human: conversations.filter(c => c.human_mode).length,
    maya: conversations.filter(c => !c.human_mode).length,
    lead: conversations.filter(c => c.lead_created).length,
  };

  const fmtDate = ts => { try { return new Date(ts).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

  if (openConv) {
    return <ConversationChat campaign={campaign} api={api} conv={openConv} onBack={() => { setOpenConv(null); fetchConversations(); }} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">📬 Conversas da Maya</h3>
          <p className="text-sm text-gray-500 mt-0.5">Histórico de todas as conversas iniciadas pela Maya com prospects desta campanha. Você pode assumir qualquer conversa.</p>
        </div>
        <Button size="sm" variant="outline" onClick={fetchConversations}>🔄 Atualizar</Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input className="w-52 text-sm" placeholder="🔍 Buscar prospect…" value={search} onChange={e => setSearch(e.target.value)} />
        <div className="flex gap-1">
          {[
            ['all', 'Todas', counts.all],
            ['human', '🧑 Humano ativo', counts.human],
            ['maya', '🤖 Com Maya', counts.maya],
            ['lead', '✅ Lead criado', counts.lead],
          ].map(([v, l, c]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === v ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {l} <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${filter === v ? 'bg-blue-500' : 'bg-gray-200'}`}>{c}</span>
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">⏳ Carregando conversas…</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-gray-400">
          <p className="text-4xl mb-3">📭</p>
          <p className="font-semibold text-gray-700">{conversations.length === 0 ? 'Nenhuma conversa ainda' : 'Nenhum resultado para este filtro'}</p>
          <p className="text-sm mt-1 text-gray-500">
            {conversations.length === 0 ? 'As conversas aparecerão aqui quando prospects aceitarem os convites e a Maya iniciar a interação.' : 'Tente outro filtro.'}
          </p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(conv => (
            <Card key={conv.id} className="border border-gray-200 hover:border-blue-300 transition-colors cursor-pointer" onClick={() => setOpenConv(conv)}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {(conv.prospect_name || '?').charAt(0).toUpperCase()}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900 text-sm">{conv.prospect_name}</p>
                      {conv.human_mode && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-200 font-medium">🧑 Humano ativo</span>
                      )}
                      {!conv.human_mode && conv.message_count > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 font-medium">🤖 Maya</span>
                      )}
                      {conv.interest_level && (
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${INTEREST_COLORS[conv.interest_level] || 'bg-gray-100 text-gray-600'}`}>
                          🎯 {conv.interest_level}
                        </span>
                      )}
                      {conv.lead_created && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 font-medium">✅ Lead</span>
                      )}
                    </div>
                    {conv.prospect_headline && <p className="text-xs text-gray-500 truncate mt-0.5">{conv.prospect_headline}</p>}
                    {conv.last_message_preview && (
                      <p className="text-xs text-gray-400 mt-1 truncate">"{conv.last_message_preview}"</p>
                    )}
                  </div>
                  {/* Meta */}
                  <div className="flex flex-col items-end gap-1 shrink-0 text-right">
                    <span className="text-xs font-semibold text-blue-600">{conv.message_count || 0} msgs</span>
                    <span className="text-xs text-gray-400">{fmtDate(conv.last_message_at)}</span>
                    <span className="text-xs text-blue-500 hover:text-blue-700">Ver conversa →</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Maya Panel ──────────────────────────────────────────────────────────

const MAYA_TABS = [
  { id: 'conversations', label: '📬 Conversas' },
  { id: 'learning',      label: '🧠 Aprendizado' },
  { id: 'flow',          label: '💬 Fluxo' },
  { id: 'chat',          label: '🤖 Testar' },
];

export default function MayaPanel({ campaign, api }) {
  const [activeTab, setActiveTab] = useState('conversations');

  return (
    <div className="space-y-5">
      {/* Maya header */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-teal-500 rounded-2xl px-6 py-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-2xl">🤖</div>
        <div>
          <p className="text-white text-xl font-bold">Maya SDR</p>
          <p className="text-white/80 text-sm">Assistente de IA · Campanha: {campaign.name}</p>
        </div>
        <div className="ml-auto text-right">
          <p className="text-white/60 text-xs">Powered by</p>
          <p className="text-white font-semibold text-sm">Groq · Llama 3.3</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit flex-wrap">
        {MAYA_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === tab.id ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'conversations' && <ConversationsTab campaign={campaign} api={api} />}
      {activeTab === 'learning' && <MayaLearningTab campaign={campaign} api={api} />}
      {activeTab === 'flow' && <FlowBuilderTab campaign={campaign} api={api} />}
      {activeTab === 'chat' && <MayaChatTab campaign={campaign} api={api} />}
    </div>
  );
}
